import React, { useMemo, useState } from 'react';

const TIERS = ['bronze', 'silver', 'gold', 'platinum'];

/**
 * Reusable visibility editor: pick which tiers can see an item and/or pick
 * specific users. An item is visible when the viewer matches a selected tier
 * OR is one of the selected users.
 *
 * Props:
 *  - visibleTiers: string[]
 *  - visibleUserIds: string[]
 *  - users: array of { id, name, email }
 *  - onChange: ({ visibleTiers, visibleUserIds }) => void
 *  - hiddenWhenEmpty: when true, an item with no tiers and no users is hidden
 */
export default function VisibilityEditor({
  visibleTiers = [],
  visibleUserIds = [],
  users = [],
  onChange,
  hiddenWhenEmpty = true,
}) {
  const [userFilter, setUserFilter] = useState('');

  const toggleTier = (tier) => {
    const next = visibleTiers.includes(tier)
      ? visibleTiers.filter((t) => t !== tier)
      : [...visibleTiers, tier];
    onChange({ visibleTiers: next, visibleUserIds });
  };

  const toggleUser = (userId) => {
    const next = visibleUserIds.includes(userId)
      ? visibleUserIds.filter((id) => id !== userId)
      : [...visibleUserIds, userId];
    onChange({ visibleTiers, visibleUserIds: next });
  };

  const clearUsers = () => onChange({ visibleTiers, visibleUserIds: [] });

  const filteredUsers = useMemo(() => {
    const q = userFilter.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        (u.name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q)
    );
  }, [users, userFilter]);

  const selectedNames = users
    .filter((u) => visibleUserIds.includes(u.id))
    .map((u) => u.name || u.email);

  return (
    <div className="visibility-editor">
      <label className="form-label">Visibility</label>

      <div className="visibility-tiers">
        <span className="visibility-hint">Visible to tiers:</span>
        {TIERS.map((tier) => (
          <label key={tier} className="checkbox-inline">
            <input
              type="checkbox"
              checked={visibleTiers.includes(tier)}
              onChange={() => toggleTier(tier)}
            />
            {tier.charAt(0).toUpperCase() + tier.slice(1)}
          </label>
        ))}
      </div>

      <div className="visibility-user">
        <div className="visibility-user-header">
          <span className="visibility-hint">Also visible to specific users:</span>
          {visibleUserIds.length > 0 && (
            <button type="button" className="btn btn-sm" onClick={clearUsers}>
              Clear ({visibleUserIds.length})
            </button>
          )}
        </div>
        <input
          type="text"
          className="visibility-user-filter"
          placeholder="Filter users..."
          value={userFilter}
          onChange={(e) => setUserFilter(e.target.value)}
        />
        <div className="picker-table-wrap">
          {filteredUsers.length === 0 ? (
            <p className="picker-empty">No matching users.</p>
          ) : (
            <table className="picker-table">
              <thead>
                <tr>
                  <th className="picker-check" scope="col"><span className="sr-only">Selected</span></th>
                  <th scope="col">Name</th>
                  <th scope="col">Email</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => {
                  const checked = visibleUserIds.includes(u.id);
                  return (
                    <tr
                      key={u.id}
                      className={checked ? 'row-selected' : undefined}
                      onClick={() => toggleUser(u.id)}
                    >
                      <td className="picker-check">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleUser(u.id)}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`Show to ${u.name || u.email}`}
                        />
                      </td>
                      <td>{u.name}</td>
                      <td className="picker-muted">{u.email}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <small className="visibility-note">
        {visibleTiers.length === 0 && visibleUserIds.length === 0
          ? hiddenWhenEmpty
            ? 'Nothing selected — this item is hidden until you pick a tier or user.'
            : 'Nothing selected — visible to everyone.'
          : [
              visibleTiers.length > 0 ? `Tiers: ${visibleTiers.join(', ')}` : null,
              selectedNames.length > 0 ? `Users: ${selectedNames.join(', ')}` : null,
            ]
              .filter(Boolean)
              .join(' • ')}
      </small>
    </div>
  );
}
